// No commerce implementation lives here: load original modules and preserve their handlers.
export function createModuleGate(manifest, importModule = (url) => import(url)) {
  const requests = new Map();
  const states = new Map();
  const load = (name) => {
    let state = states.get(name);
    if (state) return state.promise;
    state = { ready: false, promise: null };
    states.set(name, state);
    const dependency = name === 'commerce' ? load('cart') : name === 'search' ? load('commerce') : Promise.resolve();
    state.promise = dependency.then(() => Promise.all((manifest[name] || []).map((url) => {
      if (!requests.has(url)) {
        const request = importModule(url);
        requests.set(url, request);
        request.catch(() => requests.delete(url));
      }
      return requests.get(url);
    }))).then(() => { state.ready = true; }).catch((error) => {
      states.delete(name);
      throw error;
    });
    return state.promise;
  };
  return {
    load,
    ready: (name) => states.get(name)?.ready === true,
    prime() {
      // Desktop keeps parallel requests; dependency ordering is needed only for mobile intent.
      for (const urls of Object.values(manifest)) {
        if (!Array.isArray(urls)) continue;
        for (const url of urls) {
          if (requests.has(url)) continue;
          const request = importModule(url);
          requests.set(url, request);
          request.catch(() => requests.delete(url));
        }
      }
    },
  };
}

export function installMobileGate(doc, win, manifest, importModule) {
  const gate = createModuleGate(manifest, importModule);
  const mobile = win.matchMedia('(max-width: 749px)');
  const commerceSelector = 'product-card, quick-add-component, product-form-component, variant-picker, media-gallery, slideshow-component';
  const pendingActions = new WeakSet();
  const report = (error) => console.warn('[LIMITLESS mobile modules]', error);
  const loadAll = () => {
    gate.prime();
    return Promise.all(Object.keys(manifest).filter((name) => Array.isArray(manifest[name])).map(gate.load)).catch(report);
  };
  if (!mobile.matches || win.Shopify?.designMode || !win.IntersectionObserver) {
    loadAll();
    return gate;
  }
  const groupFor = (target) => {
    if (!(target instanceof win.Element)) return null;
    if (target.closest('search-button, #search-modal')) return 'search';
    if (target.closest(commerceSelector)) return 'commerce';
    if (target.closest('[data-testid="cart-drawer-trigger"], #cart-drawer')) return 'cart';
    return null;
  };
  const fallback = (name, target) => {
    if (name === 'search') {
      const form = doc.querySelector('#search-modal form');
      const input = form?.querySelector('input[name="q"]');
      const url = new URL(form?.action || '/search', win.location.href);
      if (input?.value) url.searchParams.set('q', input.value);
      win.location.assign(url.href);
    } else if (name === 'cart') {
      win.location.assign(doc.querySelector('a[href$="/cart"]')?.href || manifest.routes?.cart || '/cart');
    } else {
      const card = target.closest('product-card');
      const link = card?.querySelector('a[href*="/products/"]');
      if (link) win.location.assign(link.href);
      else report(new Error('Product modules unavailable; original form remains available for retry.'));
    }
  };
  const completeAction = (name, target, action) => {
    if (pendingActions.has(target)) return;
    pendingActions.add(target);
    let timeout;
    const deadline = new Promise((_, reject) => {
      timeout = win.setTimeout(() => reject(new Error('Module loading timeout')), 12000);
    });
    Promise.race([gate.load(name), deadline]).then(() => {
      if (target.isConnected) action();
    }).catch((error) => { report(error); fallback(name, target); }).finally(() => {
      win.clearTimeout(timeout);
      pendingActions.delete(target);
    });
  };
  const intent = (event) => {
    const name = groupFor(event.target);
    if (name && !gate.ready(name)) gate.load(name).catch(report);
  };
  win.addEventListener('pointerdown', intent, { capture: true, passive: true });
  win.addEventListener('focusin', intent, { capture: true });
  // Window capture runs before the theme's delegated document handlers.
  // Cancel only while pending; after load the original control/handler runs once.
  const activate = (event) => {
    const name = groupFor(event.target);
    if (!name || gate.ready(name)) return;
    if (event.type === 'click' && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    const target = event.target.closest('button, input, select, summary, a, form') || event.target;
    // Ordinary product links remain native and never wait for enhancement.
    if (name === 'commerce' && target.matches('a[href]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    completeAction(name, target, () => {
      if (event.type === 'click') target.click();
      else if (event.type === 'submit' && target.requestSubmit) target.requestSubmit(submitter);
      else target.dispatchEvent(new win.Event(event.type, { bubbles: true, cancelable: true }));
    });
  };
  for (const type of ['click', 'change', 'submit']) win.addEventListener(type, activate, { capture: true });
  win.addEventListener('keydown', (event) => {
    if (!event.metaKey || event.key !== 'k' || gate.ready('search')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const dialog = doc.getElementById('search-modal');
    if (dialog) completeAction('search', dialog, () => dialog.showDialog());
  }, { capture: true });
  const observer = new win.IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    gate.load('commerce').then(() => observer.disconnect()).catch(report);
  }, { rootMargin: '250px 0px' });
  // Hidden search/cart products must not warm commerce on initial load.
  for (const element of doc.querySelectorAll('#MainContent product-card, #MainContent product-form-component, #MainContent slideshow-component')) observer.observe(element);
  mobile.addEventListener('change', () => { if (!mobile.matches) { observer.disconnect(); loadAll(); } });
  return gate;
}

const config = typeof document !== 'undefined' && document.getElementById('limitless-mobile-modules');
if (config) installMobileGate(document, window, JSON.parse(config.textContent));
